/**
 * MilestoneGrid — B-2026-0075
 *
 * Displays the athlete's full badge collection:
 *   - Earned badges: full-color with achieved_at tooltip.
 *   - Unearned badges: grayscale with requirement text.
 *
 * Used in AthleteDashboard.tsx below the score timeline.
 */

import { useState } from 'react';
import type { MilestoneItem } from '@/services/milestonesApi';
import { formatDate } from '@/utils/formatters';

// ---------------------------------------------------------------------------
// Badge card
// ---------------------------------------------------------------------------

interface BadgeCardProps {
  milestone: MilestoneItem;
}

function BadgeCard({ milestone }: BadgeCardProps) {
  const [hovered, setHovered] = useState(false);
  const earned = milestone.earned;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={
        earned && milestone.achieved_at
          ? `Earned ${formatDate(milestone.achieved_at)}`
          : milestone.requirement
      }
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        padding: '16px 12px',
        borderRadius: '14px',
        backgroundColor: earned
          ? 'var(--color-dark-card)'
          : 'var(--color-dark-elevated-2)',
        border: earned
          ? '1px solid rgba(30,215,96,0.18)'
          : '1px solid var(--color-border-subtle)',
        opacity: earned ? 1 : 0.55,
        filter: earned ? 'none' : 'grayscale(0.9)',
        transition: 'transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease',
        transform: hovered && earned ? 'translateY(-3px)' : 'none',
        boxShadow:
          hovered && earned
            ? '0 8px 24px rgba(30,215,96,0.14)'
            : 'var(--shadow-card)',
        cursor: earned ? 'default' : 'help',
        minWidth: 0,
      }}
    >
      {/* Icon */}
      <span
        style={{
          fontSize: '28px',
          lineHeight: 1,
          filter: earned ? 'none' : 'grayscale(1)',
        }}
        aria-hidden
      >
        {milestone.icon_key}
      </span>

      {/* Label */}
      <span
        style={{
          fontSize: '12px',
          fontWeight: earned ? 600 : 500,
          fontFamily: 'var(--font-heading)',
          color: earned ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
          textAlign: 'center',
          lineHeight: 1.3,
          wordBreak: 'break-word',
        }}
      >
        {milestone.label}
      </span>

      {/* Achieved date pill */}
      {earned && milestone.achieved_at && (
        <span
          style={{
            fontSize: '10px',
            fontWeight: 500,
            padding: '2px 7px',
            borderRadius: '999px',
            backgroundColor: 'rgba(30,215,96,0.12)',
            color: 'var(--color-accent)',
            border: '1px solid rgba(30,215,96,0.2)',
            letterSpacing: '0.2px',
            whiteSpace: 'nowrap',
          }}
        >
          {formatDate(milestone.achieved_at)}
        </span>
      )}

      {/* Unearned requirement tooltip-trigger (visible on hover via title attr) */}
      {!earned && hovered && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'var(--color-dark-elevated-1)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '8px',
            padding: '6px 10px',
            fontSize: '11px',
            color: 'var(--color-text-secondary)',
            whiteSpace: 'nowrap',
            maxWidth: '220px',
            textAlign: 'center',
            zIndex: 20,
            pointerEvents: 'none',
            boxShadow: 'var(--shadow-modal)',
            lineHeight: 1.4,
          }}
        >
          {milestone.requirement}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MilestoneGrid
// ---------------------------------------------------------------------------

interface MilestoneGridProps {
  milestones: MilestoneItem[];
  currentStreakWeeks: number;
  /** When true, show streak counter at the top of the section. */
  showStreak?: boolean;
}

export function MilestoneGrid({
  milestones,
  currentStreakWeeks,
  showStreak = true,
}: MilestoneGridProps) {
  const earnedCount = milestones.filter((m) => m.earned).length;

  return (
    <div>
      {/* Section header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '15px',
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            Badges
          </h3>
          <span
            style={{
              fontSize: '12px',
              fontWeight: 500,
              color: 'var(--color-text-muted)',
              padding: '2px 8px',
              borderRadius: '999px',
              backgroundColor: 'var(--color-dark-elevated-2)',
              border: '1px solid var(--color-border-subtle)',
            }}
          >
            {earnedCount}/{milestones.length}
          </span>
        </div>

        {/* Streak counter */}
        {showStreak && currentStreakWeeks > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--color-accent)',
              padding: '4px 12px',
              borderRadius: '999px',
              backgroundColor: 'rgba(30,215,96,0.08)',
              border: '1px solid rgba(30,215,96,0.18)',
            }}
          >
            <span aria-hidden>🔥</span>
            {currentStreakWeeks}-week streak
          </div>
        )}
      </div>

      {/* Badge grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
          gap: '10px',
        }}
      >
        {milestones.map((m) => (
          <BadgeCard key={m.id} milestone={m} />
        ))}
      </div>

      {earnedCount === 0 && (
        <p
          style={{
            fontSize: '13px',
            color: 'var(--color-text-muted)',
            textAlign: 'center',
            padding: '12px 0 4px',
          }}
        >
          Complete your first evaluation to start earning badges.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MiniMilestoneRow — top-3 earned badges for leaderboard rows
// ---------------------------------------------------------------------------

interface MiniMilestoneRowProps {
  milestones: MilestoneItem[];
  /** Max badges to show (default 3). */
  max?: number;
}

/**
 * Compact horizontal row of the athlete's top earned badges.
 * Used inside ChannelLeaderboardTab leaderboard entries.
 */
export function MiniMilestoneRow({ milestones, max = 3 }: MiniMilestoneRowProps) {
  const earned = milestones.filter((m) => m.earned).slice(0, max);
  if (earned.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: '3px',
      }}
      aria-label={`${earned.length} badge${earned.length !== 1 ? 's' : ''}`}
    >
      {earned.map((m) => (
        <span
          key={m.id}
          title={m.label}
          style={{ fontSize: '14px', lineHeight: 1, cursor: 'default' }}
          aria-hidden
        >
          {m.icon_key}
        </span>
      ))}
    </div>
  );
}
